import React from 'react';
import { Document, Page, View, Text } from '@react-pdf/renderer';
import { commonStyles, COLORS, formatExamDate, historyToString, calculateVolume, formatListItems, buildCombinedReason, getFacilityDisplayName } from '../utils/pdfHelpers';
import { PdfPageHeader, PdfPatientBox, PdfCallReport, PdfAccentSectionHeader, PdfSectionHeader, PdfLabelValue, PdfMeasurementBox, PdfCommentsBox, PdfFooter } from '../utils/PdfSharedComponents';

const ABNORMAL_VERTEBRAL = ['Retrograde flow', 'Biphasic (Rabbit Ears)'];

// Mirrors the calculation in CarotidForm.js — computed here so the PDF
// is never dependent on stenosisDoppler being written to form state first.
function calculateDopplerStenosis(psv, edv, icaCcaRatio, stenosis2D) {
  const psvNum = parseFloat(psv) || 0;
  const icaRatio = parseFloat(icaCcaRatio) || 0;
  if (stenosis2D === 'Near occlusion') return 'Near occlusion';
  if (stenosis2D === 'Total occlusion') return 'Total occlusion';
  if (psvNum > 230 && icaRatio > 4.0 &&
      (stenosis2D === '50-69%' || stenosis2D === '>70% but less than near occlusion')) {
    return '>70% but less than near occlusion';
  }
  if ((psvNum >= 180 && psvNum <= 230 && stenosis2D === '50-69%' && icaRatio >= 2.0 && icaRatio <= 4.0) ||
      (psvNum >= 125 && psvNum <= 180 && icaRatio >= 2.0)) {
    return '50-69%';
  }
  if (psvNum < 180 && icaRatio < 2.0 && stenosis2D === 'Less than 50%') return 'Less than 50%';
  if (psvNum < 180 && icaRatio < 2.0 && stenosis2D === 'None') return 'None';
  return 'Indeterminate';
}

export function CarotidPDF({ data, facilityInfo }) {
  const reason = buildCombinedReason(data.reason, data.history);

  const rc = data.rightCarotid || {};
  const lc = data.leftCarotid || {};

  const rcDopplerStenosis = calculateDopplerStenosis(rc.psv, rc.edv, rc.icaCcaRatio, rc.stenosis2D);
  const lcDopplerStenosis = calculateDopplerStenosis(lc.psv, lc.edv, lc.icaCcaRatio, lc.stenosis2D);
  const va = data.vertebralArteries || {};

  const measurementsText = [
    rc.psv ? `Right ICA PSV: ${rc.psv} cm/s` : null,
    rc.edv ? `Right ICA EDV: ${rc.edv} cm/s` : null,
    rc.icaCcaRatio ? `Right ICA/CCA ratio: ${rc.icaCcaRatio}` : null,
    lc.psv ? `Left ICA PSV: ${lc.psv} cm/s` : null,
    lc.edv ? `Left ICA EDV: ${lc.edv} cm/s` : null,
    lc.icaCcaRatio ? `Left ICA/CCA ratio: ${lc.icaCcaRatio}` : null,
  ].filter(Boolean).join('\n');

  // STENOSIS_OPTIONS in CarotidForm.js is 'None' | 'Less than 50%' | '50-69%' |
  // '>70% but less than near occlusion' | 'Near occlusion' | 'Total occlusion' —
  // 'None' (and the Doppler-calculated equivalent) is the only normal reading.
  const isStenosisAbnormal = (stenosis) => !!stenosis && stenosis !== 'None';

  return (
    <Document>
      <Page size="LETTER" style={commonStyles.page}>
        <PdfPageHeader facilityInfo={facilityInfo} examTitle="CAROTID DOPPLER ULTRASOUND WORKSHEET" />
        <PdfPatientBox
          firstName={data.firstName}
          lastName={data.lastName}
          mrn={data.mrn}
          examDate={data.examDate}
          technologist={data.technologist}
          studentWorksheet={data.studentWorksheet}
          supervisingSonographer={data.supervisingSonographer}
          reason={reason}
        />
        <PdfCallReport show={data.callReport} phoneNumber={data.phoneNumber} />

        <View style={commonStyles.twoCol}>
          <View style={commonStyles.colLeft}>
            <PdfMeasurementBox title="DOPPLER MEASUREMENTS">
              <Text style={commonStyles.infoBoxText}>{measurementsText || 'None recorded'}</Text>
            </PdfMeasurementBox>
            <PdfCommentsBox comments={data.comments} />
          </View>

          <View style={commonStyles.colRight}>
            <PdfAccentSectionHeader title="RIGHT CAROTID" />
            {rc.endarterectomy && <PdfLabelValue label="Prior endarterectomy:" value="Yes" forceBlack />}
            <PdfLabelValue label="Plaque:" value={rc.plaque || 'None'} isAbnormal={rc.plaque && rc.plaque !== 'None'} />
            <PdfLabelValue label="Stenosis (2D):" value={rc.stenosis2D || ''} isAbnormal={isStenosisAbnormal(rc.stenosis2D)} />
            <PdfLabelValue label="Stenosis (Doppler):" value={rcDopplerStenosis} isAbnormal={isStenosisAbnormal(rcDopplerStenosis)} />
            {rc.other && <PdfLabelValue label="Other:" value={rc.other} isAbnormal />}

            <PdfAccentSectionHeader title="LEFT CAROTID" />
            {lc.endarterectomy && <PdfLabelValue label="Prior endarterectomy:" value="Yes" forceBlack />}
            <PdfLabelValue label="Plaque:" value={lc.plaque || 'None'} isAbnormal={lc.plaque && lc.plaque !== 'None'} />
            <PdfLabelValue label="Stenosis (2D):" value={lc.stenosis2D || ''} isAbnormal={isStenosisAbnormal(lc.stenosis2D)} />
            <PdfLabelValue label="Stenosis (Doppler):" value={lcDopplerStenosis} isAbnormal={isStenosisAbnormal(lcDopplerStenosis)} />
            {lc.other && <PdfLabelValue label="Other:" value={lc.other} isAbnormal />}

            <PdfAccentSectionHeader title="VERTEBRAL ARTERIES" />
            <PdfLabelValue label="Right vertebral:" value={va.right || ''} isAbnormal={ABNORMAL_VERTEBRAL.includes(va.right)} />
            <PdfLabelValue label="Left vertebral:" value={va.left || ''} isAbnormal={ABNORMAL_VERTEBRAL.includes(va.left)} />
          </View>
        </View>

        <PdfFooter facilityInfo={facilityInfo} />
      </Page>
    </Document>
  );
}
